import { AdditiveBlending, Mesh, MeshDepthMaterial, ShaderMaterial } from "three";

/**
 * Fresnel "glow" shader for user representations — Spark's own head
 * (`sparkBehavior.ts`) and the remote-participant heads
 * (`networkUsersRenderer.ts`). Both draw `public/avatars/head.glb`, which is
 * Unity's `Runtime/AI/Spark/head13.obj` exported verbatim (byte-identical
 * position bounds, identity node transform).
 *
 * The shader is a composite of two sources, and the split is deliberate:
 *
 * **The fresnel rim** is the Campfire website client's
 * (`Campfire-Website/web-client/scripts/headShader.js`) — an eyeballed
 * approximation of Unity's look, not a port of it. It is kept verbatim because
 * it is the look that ships and has been accepted; see "Known divergences".
 *
 * **The vertical fade** IS a port, of Unity's
 * `Content/Gizmos/Materials/HeadShader.shadergraph`. That graph masks the
 * surface alpha by
 *
 *     smoothstep(Edge1, Edge2, objectSpaceY + Shift)
 *
 * taken from a `Position` node whose `m_Space: 0` is OBJECT space — so the
 * fade is fixed to the model, not to the camera. It is what makes the torso
 * disappear below the shoulders and leaves a floating head. The website
 * shader had no counterpart, and without it the body's fresnel rim stays
 * faintly lit all the way down (measured: alpha 0.04–0.10 along the whole
 * torso silhouette, which additive blending paints as visible edges — the
 * 2026-09-04 report). Unity is exactly 0.0 there.
 *
 * ⚠ The mask constants are in the mesh's OWN object space and do not transfer
 * between meshes. Unity proves this by using the same shader for the device
 * frames through `Phone Material.mat` with a completely different triple
 * (Edge1 0, Edge2 0.06, Shift 0.03) for `Display`/`Remote`/`TabletPrefab`. The
 * web port never hits that case — its device frames take a plain
 * `MeshBasicMaterial` (`networkUsersRenderer.createAvatarScreenMaterial`) —
 * but a future caller pointing this material at anything other than
 * `head.glb` must supply that mesh's own values rather than reuse these.
 *
 * ⚠ `m_AllowMaterialOverride: false` on the graph's `UniversalTarget`, so the
 * `_Surface` / `_ZWrite` / `_Cull` / `_SrcBlend` / `_DstBlend` floats
 * serialized into both `HeadMat.mat` files are INERT leftovers — do not read
 * them as Unity's render state. The graph's own settings win: Transparent,
 * Alpha blend, ZWrite off, front faces only. The `Vector1_*` property
 * overrides below ARE live; those are graph properties, always exposed.
 * ⚠ Three of them are keyed by an `m_OverrideReferenceName` rather than by
 * their `Vector1_*`/`Color_*` default: `FresnelPower`, `AlphaPower` and
 * `UserColor` (the `Color_2AA0AEE` entry still in `m_Colors` is a stale key
 * Unity never prunes). The three the fade reads — Edge1, Edge2, Shift — have
 * no override name, so they ARE their `Vector1_*` keys.
 *
 * Known divergences from Unity, each deliberate and none of them the fade:
 * - Blending. Unity is Transparent + Alpha (`SrcAlpha, OneMinusSrcAlpha`);
 *   this is additive, so the head reads as a glow rather than a ghost and
 *   never darkens what is behind it. Alpha still scales the contribution in
 *   both — three's non-premultiplied `AdditiveBlending` is
 *   `blendFuncSeparate(SRC_ALPHA, ONE, ONE, ONE)` — which is why masking
 *   alpha is enough to make the fade real here. Measured in the browser
 *   against this repo's own three build: a = 0/0.25/0.5/0.75/1 over black
 *   renders 0/64/128/191/255, i.e. exactly zero contribution at a = 0.
 *   Against a DARK backdrop additive and alpha-blend are numerically
 *   identical, which is why the wrong blend mode has never been noticed; they
 *   diverge only over bright content. Switching to `NormalBlending` for true
 *   parity is its own change with its own before/after — not part of the
 *   fade.
 * - The fresnel curve. Unity is `pow(1 - saturate(dot(N, V)), FresnelPower)`
 *   over WORLD-space vectors; this is `pow(1 - 0.7 * abs(dot(N, V)), 0.9)`
 *   over VIEW-space ones. ⚠ `FresnelPower` is not the graph's 2.0 default —
 *   BOTH materials override it (`m_OverrideReferenceName: FresnelPower`, so
 *   its material key is the plain name rather than `Vector1_1E048D8E`):
 *   Spark ships **0.38** and the participant head **0.99**. So the web's 0.9
 *   is close to the participant curve and far softer than it should be for
 *   Spark, whose real falloff is softer still. Recorded rather than changed —
 *   the rim is the accepted look and this fix is the fade.
 * - Unity's `FresnelOpacity` (0.728 for Spark, 1.0 for participants) scales
 *   EMISSION only, and its `AlphaPower` is 1.0 in both materials, so neither
 *   has a counterpart here.
 * - The `poseNormal` term below multiplies in a second, weaker vertical
 *   gradient that Unity does not have, and — because it runs an object-space
 *   position through the projection matrix — it drifts with the viewport
 *   aspect ratio. Left alone: it is part of the accepted rim look, and the
 *   Unity mask is the dominant term regardless.
 */

/**
 * Unity `Runtime/AI/Spark/HeadMat.mat` (`SparkBehavior.prefab`, guid
 * 7b27520c4d6252742824281290d077c1). Fully transparent at object Y ≤ -3.0 —
 * the widest slab of `head13`, i.e. the shoulder line — and fully opaque from
 * the jaw at object Y ≥ -0.3.
 */
const K_SPARK_HEAD_FADE = { edge1: 3.6, edge2: 6.3, shift: 6.6 } as const;

/**
 * Unity `Content/Gizmos/Materials/HeadMat.mat` (`HeadPrefab` / `OculusPrefab`,
 * guid fd4b70767a6219442a00b2a6c5ee8161). Same lower edge as Spark's; reaches
 * full opacity a little higher, at object Y ≥ +0.4.
 */
const K_PARTICIPANT_HEAD_FADE = { edge1: 3.6, edge2: 7.0, shift: 6.6 } as const;

/** Which Unity `HeadMat.mat` a material instance is standing in for. */
export type AvatarGlowVariant = "spark" | "participant";

const VSHADER = `
varying vec3 vNormal;
varying vec3 vPositionNormal;
varying vec3 poseNormal;
varying vec2 vUv;
// Unity's Position node reads OBJECT space (m_Space: 0), so this is the raw
// attribute — never a modelMatrix / modelViewMatrix product. The head is
// scaled on its NODE (HEAD_LOCAL_SCALE), which leaves the attribute alone, so
// Unity's constants transfer with no rescaling.
varying float vObjectY;

void main() {
	vUv = uv;
	vec4 localPosition = vec4( position, 1.);
	vObjectY = position.y;
 	vNormal = normalize( normalMatrix * normal );
	vPositionNormal = normalize(( modelViewMatrix * localPosition ).xyz);
	poseNormal = normalize(( projectionMatrix * localPosition ).xyz);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);

}
`;

const FSHADER = `
varying vec3 vNormal;
varying vec3 vPositionNormal;
varying vec2 vUv;
varying vec3 poseNormal;
varying float vObjectY;

uniform float uFadeEdge1;
uniform float uFadeEdge2;
uniform float uFadeShift;

void main() {
	float bias = 1.0;
	float power = 0.9;
	float scale = -0.7;
	vec3 glowColor = vec3(1, 1, 1);
	float a = pow( bias + scale * abs(dot(vNormal, vPositionNormal)), power ) * ( poseNormal.y + 2.2);
	float a_shifted = smoothstep(0.9, 3.5, a);
	// Unity HeadShader.shadergraph: Position(object).y -> Add(Shift) ->
	// Smoothstep(Edge1, Edge2) -> Multiply into SurfaceDescription.Alpha.
	float fade = smoothstep(uFadeEdge1, uFadeEdge2, vObjectY + uFadeShift);
    gl_FragColor = vec4( glowColor, a_shifted * fade );
}
`;

export function createAvatarGlowMaterial(variant: AvatarGlowVariant): ShaderMaterial {
  const fade = variant === "spark" ? K_SPARK_HEAD_FADE : K_PARTICIPANT_HEAD_FADE;
  const material = new ShaderMaterial({
    vertexShader: VSHADER,
    fragmentShader: FSHADER,
    transparent: true,
    uniforms: {
      uFadeEdge1: { value: fade.edge1 },
      uFadeEdge2: { value: fade.edge2 },
      uFadeShift: { value: fade.shift },
    },
  });
  material.blending = AdditiveBlending;
  // ⚠ `premultipliedAlpha` MUST stay at three's default of false, and this is
  // the one line in the file whose failure is silent. Non-premultiplied
  // additive is `blendFuncSeparate(SRC_ALPHA, ONE, ONE, ONE)`, so alpha
  // scales the contribution and the vertical fade above is real; premultiplied
  // is `(ONE, ONE, ONE, ONE)`, which ignores alpha entirely — measured, a
  // full-white quad renders 255 at a = 0. Setting it true would leave the mask
  // computed, wired, uniform-fed and doing NOTHING, with the torso lit again
  // and nothing anywhere reporting it.
  // Unity's shader graph uses LEqual (`m_ZTestMode: 4`). Keep the glow out of
  // the depth buffer, but test it against opaque scene geometry so models in
  // front of Spark/participants occlude their heads normally.
  material.depthTest = true;
  material.depthWrite = false;
  material.name = `avatar.glow.${variant}`;
  return material;
}

let m_headShadowDepthMaterial: MeshDepthMaterial | null = null;

/** The glow shader is additive and does not write depth — use a depth material
 *  in the shadow pass so head geometry still casts (Unity `HeadPrefab` parity). */
export function configureAvatarHeadShadowCast(mesh: Mesh): void {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  if (!m_headShadowDepthMaterial) {
    m_headShadowDepthMaterial = new MeshDepthMaterial();
    m_headShadowDepthMaterial.name = "avatar.head.shadowDepth";
  }
  mesh.customDepthMaterial = m_headShadowDepthMaterial;
}
